import React, { useState, useEffect, useCallback } from 'react';
import { reportService } from '../../services/reportService';
import { userService } from '../../services/api';
import DateRangeFilter from './DateRangeFilter';
import { Download, AlertTriangle } from 'lucide-react';
import SortableHeader, { useSort } from './SortableHeader';

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);

const TimeLogReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [userId, setUserId] = useState('');
  const [billableFilter, setBillableFilter] = useState(''); // '', 'true', 'false'
  const [billedFilter, setBilledFilter] = useState('');     // '', 'true', 'false'
  const [users, setUsers] = useState([]);

  // Load users for dropdown
  useEffect(() => {
    userService.getUsers().then(res => {
      setUsers(res.data || []);
    }).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (userId) params.user_id = userId;
      if (billableFilter !== '') params.is_billable = billableFilter;
      if (billedFilter !== '') params.is_billed = billedFilter;
      const response = await reportService.getTimeLog(params);
      setData(response.data);
    } catch (err) {
      console.error('Error fetching time log:', err);
      setError('Failed to load Time Log report');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, userId, billableFilter, billedFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const { sortConfig, requestSort, sortItems } = useSort('logged_at', 'desc');

  const handleDateChange = ({ start_date, end_date }) => {
    setStartDate(start_date);
    setEndDate(end_date);
  };

  const handleExportCSV = () => {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (userId) params.user_id = userId;
    if (billableFilter !== '') params.is_billable = billableFilter;
    if (billedFilter !== '') params.is_billed = billedFilter;
    reportService.downloadCSV(reportService.getTimeLog, params, 'time_log_report.csv');
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 w-48 bg-gray-200 rounded" />
        <div className="h-64 bg-gray-100 rounded-lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="mx-auto h-8 w-8 text-red-400 mb-2" />
        <p className="text-gray-500">{error}</p>
        <button onClick={fetchData} className="mt-2 text-yellow-600 hover:text-yellow-800 text-sm">Retry</button>
      </div>
    );
  }

  const { summary, items } = data || { summary: {}, items: [] };
  const sortedItems = sortItems(items);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">Time Log Detail</h2>
        <button onClick={handleExportCSV} className="flex items-center gap-2 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4">
        <DateRangeFilter startDate={startDate} endDate={endDate} onChange={handleDateChange} />

        <div className="flex flex-col">
          <span className="text-xs text-gray-500 mb-1">User</span>
          <select
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:ring-1 focus:ring-yellow-500 focus:border-yellow-500"
          >
            <option value="">All Users</option>
            {users.map(u => (
              <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col">
          <span className="text-xs text-gray-500 mb-1">Billable</span>
          <div className="flex gap-1">
            {[{ label: 'All', value: '' }, { label: 'Yes', value: 'true' }, { label: 'No', value: 'false' }].map(opt => (
              <button
                key={opt.value}
                onClick={() => setBillableFilter(opt.value)}
                className={`px-3 py-1 text-sm rounded font-medium transition-colors ${
                  billableFilter === opt.value ? 'bg-yellow-100 text-yellow-800' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col">
          <span className="text-xs text-gray-500 mb-1">Billed</span>
          <div className="flex gap-1">
            {[{ label: 'All', value: '' }, { label: 'Yes', value: 'true' }, { label: 'No', value: 'false' }].map(opt => (
              <button
                key={opt.value}
                onClick={() => setBilledFilter(opt.value)}
                className={`px-3 py-1 text-sm rounded font-medium transition-colors ${
                  billedFilter === opt.value ? 'bg-yellow-100 text-yellow-800' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        <div className="bg-blue-50 rounded-lg p-4">
          <p className="text-xs text-gray-500">Entries</p>
          <p className="text-lg font-bold text-blue-700">{summary.total_entries || 0}</p>
        </div>
        <div className="bg-blue-50 rounded-lg p-4">
          <p className="text-xs text-gray-500">Total Hours</p>
          <p className="text-lg font-bold text-blue-700">{parseFloat(summary.total_hours || 0).toFixed(1)}</p>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <p className="text-xs text-gray-500">Billable</p>
          <p className="text-lg font-bold text-green-700">{parseFloat(summary.billable_hours || 0).toFixed(1)}</p>
        </div>
        <div className="bg-gray-100 rounded-lg p-4">
          <p className="text-xs text-gray-500">Non-Billable</p>
          <p className="text-lg font-bold text-gray-700">{parseFloat(summary.non_billable_hours || 0).toFixed(1)}</p>
        </div>
        <div className="bg-yellow-50 rounded-lg p-4">
          <p className="text-xs text-gray-500">Billed</p>
          <p className="text-lg font-bold text-yellow-700">{parseFloat(summary.billed_hours || 0).toFixed(1)}</p>
        </div>
        <div className="bg-orange-50 rounded-lg p-4">
          <p className="text-xs text-gray-500">Unbilled</p>
          <p className="text-lg font-bold text-orange-700">{parseFloat(summary.unbilled_hours || 0).toFixed(1)}</p>
        </div>
        <div className="bg-purple-50 rounded-lg p-4">
          <p className="text-xs text-gray-500">Total Amount</p>
          <p className="text-lg font-bold text-purple-700">{formatCurrency(summary.total_amount || 0)}</p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortableHeader label="Date" sortKey="logged_at" sortConfig={sortConfig} onSort={requestSort} />
                <SortableHeader label="User" sortKey="user_name" sortConfig={sortConfig} onSort={requestSort} />
                <SortableHeader label="Case" sortKey="case_number" sortConfig={sortConfig} onSort={requestSort} />
                <SortableHeader label="Client" sortKey="client_name" sortConfig={sortConfig} onSort={requestSort} />
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <SortableHeader label="Hours" sortKey="hours" sortConfig={sortConfig} onSort={requestSort} align="right" />
                <SortableHeader label="Rate" sortKey="hourly_rate" sortConfig={sortConfig} onSort={requestSort} align="right" />
                <SortableHeader label="Amount" sortKey="total_amount" sortConfig={sortConfig} onSort={requestSort} align="right" />
                <SortableHeader label="Billable" sortKey="is_billable" sortConfig={sortConfig} onSort={requestSort} />
                <SortableHeader label="Billed" sortKey="is_billed" sortConfig={sortConfig} onSort={requestSort} />
                <SortableHeader label="Invoice" sortKey="invoice_number" sortConfig={sortConfig} onSort={requestSort} />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {sortedItems.length === 0 ? (
                <tr><td colSpan={11} className="px-4 py-8 text-center text-gray-400">No time entries found</td></tr>
              ) : sortedItems.map((item) => (
                <tr key={item.entry_id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                    {new Date(item.logged_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{item.user_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                    {item.case_number ? `${item.case_number}` : <span className="text-gray-400">-</span>}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{item.client_name || <span className="text-gray-400">-</span>}</td>
                  <td className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate" title={item.description || ''}>
                    {item.description || <span className="text-gray-400">-</span>}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-gray-700">{parseFloat(item.hours).toFixed(1)}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-700">
                    {item.hourly_rate ? formatCurrency(item.hourly_rate) : <span className="text-gray-400">-</span>}
                  </td>
                  <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">{formatCurrency(item.total_amount)}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                      item.is_billable ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {item.is_billable ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                      item.is_billed ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {item.is_billed ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                    {item.invoice_number ? (
                      <span title={item.invoice_status}>{item.invoice_number}</span>
                    ) : <span className="text-gray-400">-</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TimeLogReport;
